import { LazyFallback } from "@msuite/ui/loading-screen";
import { localized } from "@msuite/ui/localization";
import { Popover } from "@msuite/ui/popover";
import { createColumnHelper, createMeta } from "@msuite/ui/table";
import { ProjectInvoiceTag } from "@msuite/ui/tag";
import { moment } from "@msuite/utils/date";
import { formatNumber } from "@msuite/utils/numbers";
import { getProjectInvoiceTypeString } from "@msuite/utils/projects";
import { guardString } from "@msuite/utils/strings";
import type { ProjectInvoiceWithProject } from "@msuite/utils/types";
import { lazy, Suspense } from "react";
import { InvoicesTableState } from "@/components/invoices";
import { NoteText } from "@/components/notes";
import { ProjectPopover } from "@/components/popovers/project-popover";
import { RowFiles } from "@/components/tables";
import { RowActions } from "@/sites/projects/project/panels/project-invoices/row-actions";

const UserCard = lazy(() => import("@/components/cards/user-card"));

const columnHelper = createColumnHelper<ProjectInvoiceWithProject>();

export const columns = [
	columnHelper.accessor("number", {
		cell: (cell) => cell.getValue(),
		header: localized("invoices.number"),
		id: "number",
		meta: createMeta({
			isTabular: true,
		}),
	}),
	columnHelper.accessor((row) => row._project?.name, {
		cell: (cell) => (
			<Popover
				trigger="click"
				variant="plain"
				render={() => <ProjectPopover projectId={cell.row.original.projectId} />}
			>
				<p>{cell.getValue()}</p>
			</Popover>
		),
		header: localized("projects.project"),
		id: "projectName",
	}),
	columnHelper.accessor("createdBy", {
		cell: (cell) => (
			<Suspense fallback={<LazyFallback variant="portal" />}>
				<UserCard
					variant="plain"
					userId={cell.getValue()}
				/>
			</Suspense>
		),
		header: localized("general_fields.created_by"),
		id: "createdBy",
	}),
	columnHelper.accessor("creationDate", {
		cell: (cell) => (cell.getValue() ? moment(cell.getValue()).dateFormat() : guardString("")),
		header: localized("invoices.creation_date"),
		id: "creationDate",
	}),
	columnHelper.accessor("calculatedTo", {
		cell: (cell) => (cell.getValue() ? moment(cell.getValue()).dateFormat() : guardString("")),
		header: localized("invoices.calculated_to"),
		id: "calculatedTo",
	}),
	columnHelper.accessor((row) => localized(getProjectInvoiceTypeString(row.type)), {
		cell: (cell) => (
			<ProjectInvoiceTag
				type={cell.row.original.type}
				variant="plain"
			/>
		),
		header: localized("invoices.type"),
		id: "type",
	}),
	columnHelper.accessor("progressInvoiceNumber", {
		cell: (cell) => guardString(cell.getValue()),
		header: localized("invoices.progress_invoice_number"),
		id: "progressInvoiceNumber",
		meta: createMeta({
			isNumeric: true,
		}),
	}),
	columnHelper.accessor("sum", {
		cell: (cell) => formatNumber(cell.getValue(), "currency"),
		header: localized("invoices.sum_net"),
		id: "sum",
		meta: createMeta({
			isNumeric: true,
		}),
	}),
	columnHelper.accessor("state", {
		cell: (cell) => <InvoicesTableState invoice={cell.row.original} />,
		header: localized("general_fields.state"),
		id: "state",
		meta: createMeta({
			withoutPadding: true,
		}),
	}),
	columnHelper.accessor("note", {
		cell: (cell) => (
			<NoteText
				note={guardString(cell.getValue())}
				withPopover
			/>
		),
		header: localized("invoices.note"),
		id: "note",
	}),
	columnHelper.accessor((row) => row.invoiceFiles.length, {
		cell: (cell) => (
			<RowFiles
				files={cell.row.original.invoiceFiles}
				unlink={{
					tableName: "outgoingInvoiceFiles",
					foreignKey: "outgoingInvoiceId",
					identifier: cell.row.original.id,
				}}
				parent={{ subject: "Invoice", resource: cell.row.original }}
			/>
		),
		header: localized("files.files"),
		id: "files",
	}),
	columnHelper.accessor((_, i) => `Index ${i}`, {
		cell: (cell) => (
			<RowActions
				invoice={cell.row.original}
				withLinkToProject
			/>
		),
		enableHiding: false,
		enablePinning: false,
		enableSorting: false,
		header: "",
		id: "actions",
	}),
];
